import { useQuery } from "@tanstack/react-query";
import { CountryCode, Train } from "@vygruppen/vy-train-map";
import { getTrafficguiBaseUrl } from "api/common";
import { queryFnGET } from "api/tanStackQuery/helpers";
import { queryClient } from "api/tanStackQuery/queryClient";
import { LogLevel, log } from "logging/datadogBrowserLogs";
import { useWebsocket } from "websocket/useWebsocket";
import {
  BackendIdentifier,
  TrainUpdateMessage,
  WebsocketTopic,
} from "websocket/websocketSharedWorkerTypes";

interface TrainData {
  trains: Train[];
}

const trainsQueryKey = (countryCode: CountryCode) => ["trains", countryCode];

const createIndex = (train: Train) =>
  `${train.train_id}_${train.train_date}_${train.country_code}`;

const updateTrain = (message: Train, prev: Train[]): Train[] => [
  ...prev.filter((train) => createIndex(message) !== createIndex(train)),
  message,
];

const deleteTrain = (message: Train, prev: Train[]) =>
  prev.filter((train) => createIndex(train) !== createIndex(message));

const handleTrainUpdateMessage = ({ message }: TrainUpdateMessage) => {
  const { type, train: newTrain, timestamp } = message;
  switch (type) {
    case "UPDATE":
      queryClient.setQueryData<TrainData>(
        trainsQueryKey(newTrain.country_code ?? "NO"),
        (prevState) =>
          prevState
            ? { trains: updateTrain(newTrain, prevState.trains) }
            : { trains: [newTrain] },
      );
      break;
    case "DELETE":
      queryClient.setQueryData<TrainData>(
        trainsQueryKey(newTrain.country_code ?? "NO"),
        (prevState) =>
          prevState
            ? { trains: deleteTrain(newTrain, prevState.trains) }
            : { trains: [] },
      );
      break;
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- might get weird data from backend
    default:
      log(
        LogLevel.error,
        "useRealtimeTrains/handleTrainUpdateMessage",
        `Received message with invalid type: ${type}, at ${timestamp} for train ${createIndex(
          newTrain,
        )}`,
      );
  }
};

export const useRealtimeTrains = (countryCode?: CountryCode) => {
  const { data: trainData, isSuccess: isTrainsSuccess } = useQuery({
    queryKey: trainsQueryKey(countryCode ?? "NO"),
    retry: 1,
    queryFn: ({ signal }) =>
      queryFnGET<TrainData>({
        signal,
        url: `${getTrafficguiBaseUrl()}/trainmap-backend/trains${
          countryCode ? `?countryCode=${countryCode}` : ""
        }`,
      }),
    refetchOnWindowFocus: true,
  });

  const { connected } = useWebsocket(BackendIdentifier.TRAINMAP_BACKEND, {
    // This crashes the handler if you switch country with the map open. Sue me
    [countryCode === "NO"
      ? WebsocketTopic.OTR
      : WebsocketTopic.TRAIN_UPDATE_SE]: handleTrainUpdateMessage,
  });

  return { trainData, isTrainsSuccess, websocketConnected: connected };
};
